import { UIView } from '@uirouter/react';
import cx from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import ReactModal from 'react-modal';
import PasskeyEnrollment from '~/modules/components/global/passkey-enrollment';
import Intercom from '~/modules/components/global/intercom';
import MainMenu from '~/modules/components/global/main-menu';
import MenuControls from '~/modules/components/global/menu-controls';

import UserService from '~/modules/services/user-service';
import MenuLogo from '~/modules/components/global/menu-logo';
import NotificationsPanel from '~/modules/components/global/notifications-panel';

import useToggle from '~/modules/hooks/use-toggle';

import RouteService from '~/modules/services/route-service';
import StorageService from '~/modules/services/storage-service';
import useUploadStore from '~/stores/use-upload-store';
import useNotificationsStore, { selectHasUnread } from '~/stores/use-notifications-store';
import useSocialRequestStore from '~/stores/use-social-request-store';
import PermissionsService from '~/modules/services/permission-service';
import useAuth from '~/modules/hooks/use-auth';
import SessionService from '~/modules/services/session-service';

import useShareQueue from '~/modules/hooks/use-share-queue';
import useDownloads from '~/modules/hooks/use-downloads';
import useWorkRequestQueueStore from '~/stores/use-work-request-queue-store';

/**
 * The gate, and nothing else. `MainLayoutShell` below owns every data hook -- the share queue,
 * the downloads -- and is not mounted until there is a session that owes us nothing, so a
 * session that must set up a passkey first sends no background queries at all. Gating inside
 * the same component let those hooks mount and fire before the check ran; the API refused
 * them, as it should, and `useDownloads` answered with a toast about downloads on a screen
 * that had asked for a passkey.
 */
export function MainLayout() {
	const { session } = useAuth();

	// Do not render page without session
	if( !session || _.isEmpty(session) ) {
		return null;
	}

	// Nor with one that owes us a passkey.
	//
	// `passkeyRequired` comes back on every session — an account under the requirement that
	// holds none — so this survives a reload and a new tab, where a screen that remembered the
	// answer itself would not. Nothing else mounts behind it: no menu, no page, no queries.
	//
	// The API holds the same line: until a passkey exists this session's token is good for the
	// passkey endpoints, reading the session and signing out, and answers 403 to everything
	// else -- so calling the API by hand is not a way round this screen. `api-service` swallows
	// that particular 403 without a toast, for anything that does reach it.
	if( session.passkeyRequired ) {
		return (
			<ReactModal
				isOpen
				contentLabel="Set up a passkey"
				className="px-auth-modal"
				overlayClassName="px-auth-overlay"
				bodyOpenClassName="px-body-auth-modal-open"
				htmlOpenClassName="px-html-auth-modal-open"
				shouldCloseOnOverlayClick={false}
			>
				{ /* Reloaded rather than navigated: the session says a passkey is required and
					the only way to hear otherwise is to ask the API again. */ }
				<PasskeyEnrollment onDone={() => window.location.reload()}/>
			</ReactModal>
		);
	}

	return <MainLayoutShell session={session}/>;
}

function MainLayoutShell({ session }) {

	const [, shareQueue, { isLoading: isShareQueueLoading }] = useShareQueue();

	const [isNavMaximized, setIsNavMaximized] = React.useState(StorageService.get('navLocked') === 'true' || false);
	const { logout } = useAuth();

	const menuCx = cx('px-menu', { navMaximized: isNavMaximized });

	function minimizeNav() {
		StorageService.set('navLocked', false);
		setIsNavMaximized(false);
	}
	function maximizeNav() {
		StorageService.set('navLocked', true);
		setIsNavMaximized(true);
	}

	const [
		showNotifications,
		toggleShowNotifications,
		setShowNotifications
	] = useToggle(false);

	// Get ingestUploads from zustand store
	const uploadsMap = useUploadStore((state) => state.uploads);

	// Derive the uploads array and progress - only recalculate when uploadsMap changes
	const ingestUploads = React.useMemo(() => {
		return Object.keys(uploadsMap).map((id) => ({
			id,
			...uploadsMap[id]
		}));
	}, [uploadsMap]);

	const uploadProgress = React.useMemo(() => {
		if(ingestUploads.length === 0) {
			return 0;
		}
		return ingestUploads.reduce((sum, upload) => sum + upload.progress, 0) / ingestUploads.length;
	}, [ingestUploads]);

	const hasUnreadNotifications = useNotificationsStore(selectHasUnread);

	const socialRequestQueue = useSocialRequestStore((state) => state.socialRequestQueue);
	const socialRequestQueueSize = socialRequestQueue.length;

	const workRequestQueue = useWorkRequestQueueStore((state) => state.workRequestQueue);

	const { active } = useDownloads(session._id);
	const isGuest = UserService.isGuest(session);

	const downloadCount = active?.length || 0;

	const LINKS = getLinks(session, {
		downloadCount
	}, {
		progress: uploadProgress
	});

	const shareQueueSize = !isShareQueueLoading
		? shareQueue?.length || 0
		: 0;

	const QUEUES = getQueueLinks(session, {
		shareQueueSize,
		workRequestQueueSize: workRequestQueue.length,
		socialRequestQueueSize
	});
	const NOTIFICATIONS = {
		badge: hasUnreadNotifications
	};

	return (
		<div className="px-grid">
			<div className={menuCx}>
				<MenuLogo
					logo={SessionService.getStudioLogo(session)}
					isMaximized={isNavMaximized}
				/>

				<MainMenu
					links={LINKS}
					session={session}
					queues={QUEUES}
					onLogout={logout}
					notifications={NOTIFICATIONS}
					onNotificationsClick={toggleShowNotifications}
					isNotificationsActive={showNotifications}
					isMaximized={isNavMaximized}
				/>

				<MenuControls
					isMaximized={isNavMaximized}
					onMinimizeClick={minimizeNav}
					onMaximizeClick={maximizeNav}
				/>
			</div>
			<Suspense fallback={<div></div>}>
				<UIView className="px-sidebar" name="sidebar" />
				<UIView className="px-main" name="main" data-cy="main-content" />
			</Suspense>

			{ !isGuest && (
				<div className="px-notifications">
					<NotificationsPanel
						session={session}
						isOpen={showNotifications}
						onMenuClose={() => setShowNotifications(false)}
					/>
				</div>
			)}

			<Intercom session={session} />
		</div>
	);
}

MainLayoutShell.propTypes = {
	session: PropTypes.shape({
		_id: PropTypes.string
	}).isRequired
};

const isActive = (stack) => {
	return _.get(RouteService.currentState, 'data.stack', '') === stack;
};

const displayUploadsProgress = (uploadsProgress) => {
	if( uploadsProgress === 0 || uploadsProgress === 1 ) {
		return null;
	}
	return `${Math.round(uploadsProgress * 100)}%`;
};

function getLinks(session, counts, uploads) {
	const PROJECTS_LINK = {
		sref: 'projects',
		icon: 'projects',
		label: 'Projects',
		enabled: true,
		get active() {
			return isActive('catalog');
		}
	};
	const SHARES_LINK = {
		sref: 'shares',
		icon: 'shares',
		label: 'Shares',
		enabled: true,
		get active() {
			return isActive('shares');
		}
	};
	const WORK_REQUESTS_LINK = {
		sref: 'work-requests',
		icon: 'workRequests2',
		label: 'Work Requests',
		enabled: !UserService.isGuest(session),
		get active() {
			return isActive('work-requests');
		}
	};
	const INGEST_LINK = {
		sref: 'ingest',
		icon: 'ingest',
		label: 'Ingests',
		enabled: PermissionsService.hasIngestPermission(session),
		badge: displayUploadsProgress(uploads.progress),
		get active() {
			return isActive('ingest');
		}
	};
	const DOWNLOADS_LINK = {
		sref: 'downloads',
		icon: 'downloads',
		label: 'Downloads',
		enabled: !UserService.isGuest(session),
		badge: counts.downloadCount,
		get active() {
			return isActive('downloads');
		}
	};
	const REPORTS_LINK = {
		sref: 'reports',
		icon: 'reports',
		label: 'Reports',
		enabled: PermissionsService.hasReportsPermission(session),
		get active() {
			return isActive('reports');
		}
	};
	const ADMIN_LINK = {
		sref: 'admin',
		icon: 'admin',
		label: 'Admin',
		enabled: UserService.isAdmin(session),
		get active() {
			return isActive('admin');
		}
	};
	const LINKS = [
		PROJECTS_LINK,
		SHARES_LINK,
		WORK_REQUESTS_LINK,
		INGEST_LINK,
		DOWNLOADS_LINK,
		REPORTS_LINK,
		ADMIN_LINK
	];

	return LINKS;
}

function getQueueLinks(session, queueSizes) {
	const SHARE_QUEUE = {
		sref: 'share-queue',
		icon: 'shares2',
		label: 'Share Queue',
		enabled: !UserService.isGuest(session),
		badge: queueSizes.shareQueueSize,
		get active() {
			return isActive('share-request-queue');
		}
	};
	const SOCIAL_REQUEST_QUEUE = {
		sref: 'social-request-queue',
		icon: 'social',
		label: 'Social Queue',
		enabled: false,
		badge: queueSizes.socialRequestQueueSize,
		get active() {
			return isActive('social-request-queue');
		}
	};
	const WORK_REQUEST_QUEUE = {
		sref: 'work-request-queue',
		icon: 'workRequests',
		label: 'Order Queue',
		enabled: !UserService.isGuest(session),
		badge: queueSizes.workRequestQueueSize,
		get active() {
			return isActive('work-request-queue');
		}
	};
	// QUEUES
	return [SHARE_QUEUE, SOCIAL_REQUEST_QUEUE, WORK_REQUEST_QUEUE];
}

export default MainLayout;
